
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Package, Search, Upload, Plus, Filter, MoreVertical, Trash2, 
  FileSpreadsheet, X, CheckCircle2, Loader2, Database, Layers, 
  Save, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Camera, 
  ImageIcon, RefreshCw, Edit3, Wrench, History, Eye, DollarSign,
  ArrowRight, Clock, Check, User, Calendar,
  Truck, PlusCircle, ShoppingCart, ChevronDown
} from 'lucide-react';
import { Product, ProductType, User as UserType, Plan } from './types';
import { Card, Badge, StatCard } from './SharedComponents';

export const ProductsPage = ({ products, orders, user, plans, onAddProducts, onUpdateProducts }: { products: Product[], orders: any[], user: UserType, plans: Plan[], onAddProducts: (p: Product[]) => void, onUpdateProducts?: (p: Product[]) => void }) => {
  const [search, setSearch] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isNewItemModalOpen, setIsNewItemModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [filterCategory, setFilterCategory] = useState('Todas');
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  
  const [maintenanceProduct, setMaintenanceProduct] = useState<Product | null>(null);
  const [historyProduct, setHistoryProduct] = useState<Product | null>(null);
  
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenuId(null);
        setConfirmDeleteId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(products.map(p => p.category)));
    return ['Todas', ...cats];
  }, [products]);

  const stockCalculations = useMemo(() => {
    const inUse: Record<string, number> = {};
    orders.forEach(order => {
      const isActive = ['EM_CARREGAMENTO', 'EM_ROTA', 'MONTAGEM'].includes(order.status);
      if (isActive && order.materials) {
        order.materials.forEach((m: any) => {
          inUse[m.name] = (inUse[m.name] || 0) + m.qty;
        });
      }
    });
    return inUse;
  }, [orders]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = filterCategory === 'Todas' || p.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, filterCategory]);

  const stats = useMemo(() => ({
    totalTypes: products.length,
    totalItems: products.reduce((acc: number, p: Product) => acc + p.quantity, 0),
    totalInUse: Object.values(stockCalculations).reduce((acc: number, v: number) => acc + v, 0),
    inventoryValue: products.reduce((acc, p) => acc + (p.quantity * (p.unitPrice || 0)), 0),
    consumablesCount: products.filter(p => p.type === ProductType.CONSUMABLE).length
  }), [products, stockCalculations]);

  // Limite do plano
  const currentPlan = plans.find(p => p.name === (user as any)?.companyPlan) || plans[0] || { maxItems: 100 };
  const canAddMoreItems = currentPlan.maxItems === -1 || stats.totalItems < currentPlan.maxItems;

  const executeMaintenanceMove = (id: string, qty: number) => {
    if (!onUpdateProducts) return;
    const updated = products.map(item => item.id === id ? { ...item, quantityMaintenance: item.quantityMaintenance + qty } : item);
    onUpdateProducts(updated);
    setMaintenanceProduct(null);
    setActiveMenuId(null);
  };

  const handleRemoveProduct = (id: string) => {
    if (!onUpdateProducts) return;
    onUpdateProducts(products.filter(p => p.id !== id));
    setActiveMenuId(null);
    setConfirmDeleteId(null);
  };

  const handleUpdateItem = (updatedItem: Product) => {
    if (!onUpdateProducts) return;
    
    // Se estiver editando e aumentando a quantidade, validar limite
    const oldItem = products.find(p => p.id === updatedItem.id);
    const diff = updatedItem.quantity - (oldItem?.quantity || 0);
    if (diff > 0 && currentPlan.maxItems !== -1 && (stats.totalItems + diff) > currentPlan.maxItems) {
        return alert(`Limite do plano atingido. Máximo permitido: ${currentPlan.maxItems} ativos.`);
    }

    const updated = products.map(p => p.id === updatedItem.id ? updatedItem : p);
    onUpdateProducts(updated);
    setEditingProduct(null);
  };

  return (
    <div className="space-y-6 md:space-y-8 animate-in fade-in duration-700 pb-40 p-4 md:p-8 lg:p-10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 px-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-secondary uppercase tracking-tighter italic leading-none">Inventário & Ativos</h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium italic mt-2">
            Plano {currentPlan.name}: {stats.totalItems}/{currentPlan.maxItems === -1 ? '∞' : currentPlan.maxItems} ativos cadastrados.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <button 
            onClick={() => setIsImportModalOpen(true)}
            className="flex-1 lg:flex-none bg-white border-2 border-gray-100 text-secondary px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
          >
            <Upload size={18} className="text-primary" /> Importar CSV
          </button>
          <button 
            onClick={() => {
                if (!canAddMoreItems) return alert("Limite de ativos do plano atingido. Faça upgrade para expandir.");
                setIsNewItemModalOpen(true);
            }}
            className={`flex-1 lg:flex-none px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 transition-all shadow-xl active:scale-95 ${canAddMoreItems ? 'bg-primary text-white shadow-primary/20 hover:brightness-110' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            <Plus size={18} /> Novo Item
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard title="Saldo Total" value={stats.totalItems} icon={Database} colorClass="bg-primary/5 text-primary" />
        <StatCard title="Em Eventos" value={stats.totalInUse} icon={ArrowUpCircle} colorClass="bg-amber-50 text-amber-600" />
        <StatCard title="Insumos (Fitas/Bolsas)" value={stats.consumablesCount} icon={ShoppingCart} colorClass="bg-indigo-50 text-indigo-600" />
        <StatCard title="Valor do Ativo" value={`R$ ${stats.inventoryValue.toLocaleString('pt-BR')}`} icon={DollarSign} colorClass="bg-secondary text-white" />
      </div>

      <Card className="p-0 border-none shadow-2xl bg-white rounded-[32px] md:rounded-[40px] overflow-visible">
        <div className="p-6 md:p-8 border-b border-gray-50 flex flex-col md:flex-row gap-4 justify-between items-center">
          <div className="relative flex-1 w-full group">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors" size={20} />
            <input 
              type="text" 
              placeholder="Pesquisar por nome ou SKU..." 
              className="w-full pl-16 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-[22px] outline-none focus:ring-4 focus:ring-primary/5 font-bold text-secondary transition-all text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto">
             <div className="relative flex-1 md:flex-none">
               <select 
                 className="w-full bg-gray-50 border border-gray-100 p-4 rounded-[22px] font-bold text-secondary outline-none focus:ring-4 focus:ring-primary/5 appearance-none cursor-pointer pr-12 min-w-[160px] text-sm"
                 value={filterCategory}
                 onChange={(e) => setFilterCategory(e.target.value)}
               >
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
               </select>
               <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
             </div>
          </div>
        </div>

        <div className="overflow-visible">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50/50 text-gray-400 text-[9px] uppercase font-black tracking-[0.2em] border-b border-gray-100">
                <th className="px-8 py-6">Produto / Referência</th>
                <th className="px-6 py-6">Tipo</th>
                <th className="px-6 py-6 text-center">Valor Unit.</th>
                <th className="px-6 py-6 text-center">Oficina</th>
                <th className="px-6 py-6 text-center">Disponível</th>
                <th className="px-8 py-6 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredProducts.map((p, index) => {
                const used = stockCalculations[p.name] || 0;
                const available = p.quantity - used - p.quantityMaintenance;
                const isNearBottom = index >= filteredProducts.length - 2 && filteredProducts.length > 3;

                return (
                  <tr key={p.id} className="hover:bg-primary/[0.02] transition-colors group">
                    <td className="px-8 py-7 md:py-8">
                      <div className="flex items-center gap-5">
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-[20px] md:rounded-[24px] bg-gray-100 flex items-center justify-center text-gray-300 overflow-hidden border-2 border-gray-50 shadow-inner shrink-0">
                          {p.image ? (
                            <img src={p.image} className="w-full h-full object-cover" alt={p.name} />
                          ) : (
                            <ImageIcon size={28} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-black text-secondary uppercase tracking-tight text-sm md:text-base truncate">{p.name}</p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">SKU: {p.sku} • {p.color}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-8">
                      <Badge className={p.type === ProductType.CONSUMABLE ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-blue-50 text-blue-600 border-blue-100'}>
                         {p.type || ProductType.ASSET}
                      </Badge>
                    </td>
                    <td className="px-6 py-8 text-center">
                       <p className="font-black text-secondary text-sm">R$ {(p.unitPrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    </td>
                    <td className="px-6 py-8 text-center">
                      <span className={`text-sm font-black ${p.quantityMaintenance > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{p.quantityMaintenance} un</span>
                    </td>
                    <td className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center">
                         <span className={`text-base font-black ${available <= 0 ? 'text-red-500' : 'text-secondary'}`}>
                           {available} un
                         </span>
                      </div>
                    </td>
                    <td className="px-8 py-8 text-right overflow-visible">
                      <div className="relative inline-block text-left" ref={activeMenuId === p.id ? menuRef : null}>
                        <button 
                          onClick={() => { setActiveMenuId(activeMenuId === p.id ? null : p.id); setConfirmDeleteId(null); }}
                          className={`p-3 transition-all rounded-[18px] border-2 shadow-sm ${activeMenuId === p.id ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-300 border-gray-100 hover:text-primary hover:border-primary/20 active:scale-95'}`}
                        >
                          <MoreVertical size={20} />
                        </button>
                        
                        {activeMenuId === p.id && (
                          <div 
                            className={`absolute right-0 w-64 bg-white border border-gray-100 rounded-[32px] shadow-[0_30px_60px_-12px_rgba(0,0,0,0.25)] z-[9999] overflow-hidden animate-in fade-in zoom-in-95 duration-200 border-t-4 border-t-primary ${
                              isNearBottom ? 'bottom-full mb-3' : 'top-full mt-3'
                            }`}
                          >
                             <div className="p-3 space-y-1">
                                <div className="px-5 py-4 border-b border-gray-50 mb-1 bg-gray-50/50">
                                   <p className="text-[8px] font-black text-gray-400 uppercase tracking-[0.3em] mb-1 leading-none">Ações do Core</p>
                                   <p className="text-[11px] font-black text-secondary truncate uppercase">{p.name}</p>
                                </div>
                                
                                <button 
                                  onClick={(e) => { e.stopPropagation(); setEditingProduct(p); setActiveMenuId(null); }}
                                  className="w-full text-left px-4 py-3.5 text-[10px] font-black uppercase text-secondary hover:bg-primary hover:text-white rounded-[20px] transition-all flex items-center gap-4 group/item"
                                >
                                   <div className="p-2 bg-gray-50 rounded-xl group-hover/item:bg-white/20 transition-colors">
                                     <Edit3 size={16} />
                                   </div>
                                   Editar Ativo
                                </button>

                                <button 
                                  onClick={(e) => { e.stopPropagation(); setMaintenanceProduct(p); setActiveMenuId(null); }}
                                  className="w-full text-left px-4 py-3.5 text-[10px] font-black uppercase text-amber-600 hover:bg-amber-500 hover:text-white rounded-[20px] transition-all flex items-center gap-4 group/item"
                                >
                                   <div className="p-2 bg-amber-50 rounded-xl group-hover/item:bg-white/20 transition-colors">
                                     <Wrench size={16} />
                                   </div>
                                   Mover p/ Oficina
                                </button>

                                <button 
                                  onClick={(e) => { e.stopPropagation(); setHistoryProduct(p); setActiveMenuId(null); }}
                                  className="w-full text-left px-4 py-3.5 text-[10px] font-black uppercase text-blue-600 hover:bg-blue-500 hover:text-white rounded-[20px] transition-all flex items-center gap-4 group/item"
                                >
                                   <div className="p-2 bg-blue-50 rounded-xl group-hover/item:bg-white/20 transition-colors">
                                     <History size={16} />
                                   </div>
                                   Ver Histórico
                                </button>

                                <div className="h-[1px] bg-gray-100 mx-4 my-2 opacity-50"></div>

                                {confirmDeleteId === p.id ? (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); handleRemoveProduct(p.id); }}
                                    className="w-full text-left px-4 py-3.5 text-[10px] font-black uppercase bg-red-500 text-white rounded-[20px] transition-all flex items-center gap-4 animate-in slide-in-from-right-2"
                                  >
                                     <div className="p-2 bg-white/20 rounded-xl">
                                       <CheckCircle2 size={16} />
                                     </div>
                                     Confirmar Exclusão?
                                  </button>
                                ) : (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(p.id); }}
                                    className="w-full text-left px-4 py-3.5 text-[10px] font-black uppercase text-red-500 hover:bg-red-500 hover:text-white rounded-[20px] transition-all flex items-center gap-4 group/item"
                                  >
                                     <div className="p-2 bg-red 50 rounded-xl group-hover/item:bg-white/20 transition-colors">
                                       <Trash2 size={16} />
                                     </div>
                                     Excluir do Core
                                  </button>
                                )}
                             </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {maintenanceProduct && (
        <MaintenanceModal 
          product={maintenanceProduct} 
          availableCount={maintenanceProduct.quantity - (stockCalculations[maintenanceProduct.name] || 0) - maintenanceProduct.quantityMaintenance}
          onClose={() => setMaintenanceProduct(null)} 
          onConfirm={executeMaintenanceMove}
        />
      )}

      {historyProduct && (
        <HistoryModal 
          product={historyProduct} 
          onClose={() => setHistoryProduct(null)} 
        />
      )}

      {isImportModalOpen && (
        <ImportModal onClose={() => setIsImportModalOpen(false)} onImport={(items: Product[]) => { 
            const totalRequested = items.reduce((acc, i) => acc + i.quantity, 0);
            if (currentPlan.maxItems !== -1 && (stats.totalItems + totalRequested) > currentPlan.maxItems) {
                return alert(`Erro na importação: O arquivo excede o limite de ativos do plano (${currentPlan.maxItems}).`);
            }
            onAddProducts(items); 
            setIsImportModalOpen(false); 
        }} />
      )}

      {(isNewItemModalOpen || editingProduct) && (
        <NewProductModal 
          user={user}
          initialData={editingProduct}
          onClose={() => { setIsNewItemModalOpen(false); setEditingProduct(null); }} 
          onSubmit={(newItem) => { 
            if (editingProduct) handleUpdateItem(newItem);
            else onAddProducts([newItem]); 
            setIsNewItemModalOpen(false); 
          }} 
        />
      )}
    </div>
  );
};

const MaintenanceModal = ({ product, availableCount, onClose, onConfirm }: any) => {
  const [qty, setQty] = useState(1);
  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6 text-gray-900">
      <div className="bg-white rounded-[40px] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-amber-500 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl"><Wrench size={20} /></div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tight leading-none">Mover para Oficina</h2>
              <p className="text-[8px] text-white/60 font-bold uppercase tracking-widest mt-1">Manutenção Técnica</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"><X size={24} /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="space-y-1.5">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Produto</p>
            <p className="text-sm font-bold text-secondary uppercase">{product.name}</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Quantidade a retirar do estoque ({availableCount} disponível)</label>
            <input 
              type="number" 
              min="1" 
              max={availableCount}
              className="w-full bg-gray-50 border border-gray-100 p-3.5 rounded-xl outline-none font-black text-secondary text-sm shadow-inner" 
              value={qty} 
              onChange={e => setQty(Math.min(availableCount, parseInt(e.target.value) || 0))} 
            />
          </div>
          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3.5 rounded-xl border-2 font-black text-gray-400 uppercase text-[9px] tracking-widest hover:bg-gray-50 transition-all">Cancelar</button>
            <button 
              onClick={() => onConfirm(product.id, qty)}
              className="flex-1 py-3.5 rounded-xl bg-amber-500 text-white font-black uppercase text-[9px] tracking-widest shadow-xl shadow-amber-500/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <Check size={16} /> Confirmar Envio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const HistoryItem = ({ date, type, event, qty }: any) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
     <div className="flex items-center gap-4">
        <div className={`w-2 h-10 rounded-full ${type === 'SAÍDA' ? 'bg-amber-400' : type === 'ENTRADA' ? 'bg-green-500' : 'bg-blue-400'}`}></div>
        <div>
           <p className="text-[10px] font-black text-secondary uppercase leading-none">{event}</p>
           <p className="text-[8px] text-gray-400 font-bold uppercase mt-1">{date} • {type}</p>
        </div>
     </div>
     <Badge className="bg-white text-secondary border-none font-black">{qty} UN</Badge>
  </div>
);

const HistoryModal = ({ product, onClose }: any) => {
  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6 text-gray-900">
      <div className="bg-white rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-blue-500 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl"><History size={20} /></div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tight leading-none">Histórico do Ativo</h2>
              <p className="text-[8px] text-white/60 font-bold uppercase tracking-widest mt-1">{product.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"><X size={24} /></button>
        </div>
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto scrollbar-hide">
             <HistoryItem date="10/03/2025" type="SAÍDA" event="Happy Hour Totó" qty={2} />
             <HistoryItem date="05/03/2025" type="ENTRADA" event="Devolução Festival Verão" qty={4} />
             <HistoryItem date="28/02/2025" type="MANUTENÇÃO" event="Higienização Periódica" qty={1} />
        </div>
        <div className="p-6 bg-gray-50 border-t border-gray-100 text-right">
           <button onClick={onClose} className="px-6 py-3 bg-secondary text-white rounded-xl font-black uppercase text-[10px] tracking-widest">Fechar Datalog</button>
        </div>
      </div>
    </div>
  );
};

const ImportModal = ({ onClose, onImport }: any) => {
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleProcess = () => {
    if (!file) return;
    setTimeout(() => {
      onImport([
        { id: `p-${Date.now()}`, name: 'Item Importado', quantity: 10, quantityMaintenance: 0, unitPrice: 100.0, color: 'Branca', category: 'Import', sku: 'IMP-001', lastUpdated: new Date().toISOString() }
      ]);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6 text-gray-900">
      <div className="bg-white rounded-[40px] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-800 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl"><Upload size={20} /></div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tight leading-none">Importar Ativos</h2>
              <p className="text-[8px] text-white/60 font-bold uppercase tracking-widest mt-1">Carga em Massa via CSV</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"><X size={24} /></button>
        </div>
        <div className="p-8 space-y-6">
           <div 
            onClick={() => fileInputRef.current?.click()}
            className="w-full h-44 border-4 border-dashed border-gray-100 rounded-[32px] flex flex-col items-center justify-center gap-4 text-gray-300 hover:border-primary/40 hover:text-primary cursor-pointer transition-all bg-gray-50"
           >
              <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)} />
              {file ? (
                <div className="text-center">
                   <FileSpreadsheet size={48} className="mx-auto text-green-500 mb-2" />
                   <p className="text-xs font-black text-secondary uppercase truncate max-w-[200px]">{file.name}</p>
                </div>
              ) : (
                <>
                  <FileSpreadsheet size={48} />
                  <span className="text-[10px] font-black uppercase tracking-widest">Selecionar Planilha .CSV</span>
                </>
              )}
           </div>
           <div className="flex flex-col gap-3">
              <button 
                disabled={!file}
                onClick={handleProcess}
                className="w-full py-5 bg-primary text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 disabled:opacity-50 transition-all"
              >
                Processar Dados
              </button>
              <button onClick={onClose} className="w-full py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Cancelar</button>
           </div>
        </div>
      </div>
    </div>
  );
};

const NewProductModal = ({ onClose, onSubmit, initialData, user }: { onClose: () => void, onSubmit: (p: Product) => void, initialData?: Product | null, user: UserType }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    quantity: initialData?.quantity || 0,
    unitPrice: initialData?.unitPrice || 0,
    color: initialData?.color || '',
    category: initialData?.category || 'Geral',
    sku: initialData?.sku || '',
    image: initialData?.image || '',
    type: initialData?.type || ProductType.ASSET
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const product: Product = {
      ...formData,
      id: initialData?.id || `p-${Date.now()}`,
      quantityMaintenance: initialData?.quantityMaintenance || 0,
      lastUpdated: new Date().toISOString().split('T')[0],
      company_id: user.companyId
    };
    onSubmit(product);
  };

  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6 text-gray-900">
      <div className="bg-white rounded-[40px] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-primary text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl">{initialData ? <Edit3 size={20} /> : <Plus size={20} />}</div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tight leading-none">{initialData ? 'Editar Produto' : 'Novo Produto'}</h2>
              <p className="text-[8px] text-white/60 font-bold uppercase tracking-widest mt-1">Gestão de Ativos</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"><X size={24} /></button>
        </div>
        
        <form className="p-6 space-y-6 max-h-[80vh] overflow-y-auto scrollbar-hide" onSubmit={handleSubmit}>
          
          <div className="space-y-6">
            <div className="flex flex-col items-center gap-3">
               <div 
                 onClick={() => fileInputRef.current?.click()}
                 className="w-32 h-32 rounded-[24px] border-4 border-dashed border-gray-100 flex flex-col items-center justify-center text-gray-300 hover:border-primary/40 hover:text-primary cursor-pointer transition-all bg-gray-50 overflow-hidden group relative"
               >
                 {formData.image ? (
                   <>
                    <img src={formData.image} className="w-full h-full object-cover group-hover:brightness-50 transition-all" alt="Preview" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                       <Camera size={24} className="text-white" />
                    </div>
                   </>
                 ) : (
                   <>
                     <Camera size={28} className="mb-1" />
                     <span className="text-[8px] font-black uppercase tracking-widest">Foto do Item</span>
                   </>
                 )}
               </div>
               <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoChange} />
            </div>

            <div className="space-y-1.5">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Nome do Ativo</label>
              <input required type="text" className="w-full bg-gray-50 border border-gray-100 p-3.5 rounded-xl outline-none font-bold text-secondary text-sm shadow-inner" placeholder="Ex: Tenda Piramidal 5x5" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
            </div>

            <div className="space-y-1.5">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Tipo de Material</label>
              <div className="grid grid-cols-2 gap-2">
                 <button 
                  type="button"
                  onClick={() => setFormData({...formData, type: ProductType.ASSET})}
                  className={`p-3 rounded-xl border-2 font-black uppercase text-[9px] tracking-widest flex items-center justify-center gap-2 transition-all ${formData.type === ProductType.ASSET ? 'bg-primary/5 border-primary text-primary' : 'bg-white border-gray-100 text-gray-400'}`}
                 >
                   <Package size={14} /> Ativo Fixo
                 </button>
                 <button 
                  type="button"
                  onClick={() => setFormData({...formData, type: ProductType.CONSUMABLE})}
                  className={`p-3 rounded-xl border-2 font-black uppercase text-[9px] tracking-widest flex items-center justify-center gap-2 transition-all ${formData.type === ProductType.CONSUMABLE ? 'bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-100 text-gray-400'}`}
                 >
                   <ShoppingCart size={14} /> Insumo
                 </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">SKU</label>
                <input required type="text" className="w-full p-3.5 bg-gray-50 border border-gray-100 rounded-xl outline-none font-black text-secondary text-sm shadow-inner" value={formData.sku} onChange={e => setFormData({...formData, sku: e.target.value.toUpperCase()})} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Valor Un. (R$)</label>
                <input required type="number" step="0.01" className="w-full bg-gray-50 border border-gray-100 p-3.5 rounded-xl outline-none font-black text-secondary text-sm shadow-inner" placeholder="0,00" value={formData.unitPrice} onChange={e => setFormData({...formData, unitPrice: parseFloat(e.target.value)})} />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Saldo Total</label>
                <input required type="number" min="0" className="w-full bg-gray-50 border border-gray-100 p-3.5 rounded-xl outline-none font-black text-secondary text-sm shadow-inner" value={formData.quantity} onChange={e => setFormData({...formData, quantity: parseInt(e.target.value)})} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Categoria</label>
                <input required type="text" className="w-full bg-gray-50 border border-gray-100 p-3.5 rounded-xl outline-none font-bold text-secondary text-sm shadow-inner" placeholder="Mobiliário" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} />
              </div>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3.5 rounded-xl border-2 font-black text-gray-400 uppercase text-[9px] tracking-widest hover:bg-gray-50 active:scale-95 transition-all">Descartar</button>
            <button type="submit" className="flex-1 py-3.5 rounded-xl bg-primary text-white font-black uppercase text-[9px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2">
              <Save size={16} /> Salvar Produto
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
